import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Send, MapPin, Calendar, ExternalLink, Clock, CheckCircle2, XCircle, Loader2, Briefcase, Sparkles, TrendingUp, Zap, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import './ApplicationsPage.css';

const ApplicationsPage = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [autoApplyEnabled, setAutoApplyEnabled] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      try {
        // TODO: API entegrasyonu — GET /api/AutoApply/GetApplications
        await new Promise(r => setTimeout(r, 800));
        setApplications([]);
      } catch {
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, []);

  const statusConfig = {
    applied: { label: 'Başvuruldu', icon: CheckCircle2, color: '#00C9A7', bg: 'rgba(0, 201, 167, 0.08)' },
    pending: { label: 'Beklemede', icon: Clock, color: '#D97706', bg: 'rgba(217, 119, 6, 0.08)' },
    viewed: { label: 'Görüntülendi', icon: Eye, color: '#2F3A8F', bg: 'rgba(47, 58, 143, 0.08)' },
    interview: { label: 'Mülakata Davet', icon: Sparkles, color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.08)' },
    accepted: { label: 'Kabul Edildi', icon: CheckCircle2, color: '#059669', bg: 'rgba(5, 150, 105, 0.08)' },
    rejected: { label: 'Olumsuz', icon: XCircle, color: '#DC2626', bg: 'rgba(220, 38, 38, 0.08)' },
  };

  const filtered = filter === 'all' ? applications : applications.filter(a => a.status === filter);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Stats
  const totalApplied = applications.length;
  const totalViewed = applications.filter(a => a.status === 'viewed' || a.status === 'interview' || a.status === 'accepted').length;
  const totalInterview = applications.filter(a => a.status === 'interview').length;

  const handleEnableAutoApply = () => {
    if (!consentGiven) {
      setShowConsent(true);
      return;
    }
    setAutoApplyEnabled(!autoApplyEnabled);
  };

  const handleConsent = () => {
    setConsentGiven(true);
    setAutoApplyEnabled(true);
    setShowConsent(false);
  };

  return (
    <div className="applications-page">
      <SEO
        title="Başvurularım"
        description="Yapay zeka ile yapılan iş başvurularınızı takip edin."
        path="/panel/basvurularim"
        noindex
      />
      <div className="applications__container">

        <div className="applications__header">
          <div className="applications__header-icon">
            <Send size={22} />
          </div>
          <div>
            <h1 className="applications__title">Başvurularım</h1>
            <p className="applications__subtitle">Yapay zekanın senin adına yaptığı başvuruları takip et.</p>
          </div>
        </div>

        {/* Auto Apply Toggle Card */}
        <div className="applications__auto-card">
          <div className="applications__auto-top">
            <div className="applications__auto-info">
              <div className={`applications__auto-dot ${autoApplyEnabled ? 'applications__auto-dot--on' : ''}`} />
              <div>
                <span className="applications__auto-label">Otomatik Başvuru</span>
                <span className={`applications__auto-status ${autoApplyEnabled ? 'applications__auto-status--on' : ''}`}>
                  {autoApplyEnabled ? 'Aktif' : 'Pasif'}
                </span>
              </div>
            </div>
            <button
              className={`applications__auto-toggle ${autoApplyEnabled ? 'applications__auto-toggle--on' : ''}`}
              onClick={handleEnableAutoApply}
            >
              <span className="applications__auto-toggle-thumb" />
            </button>
          </div>
          <p className="applications__auto-desc">
            {autoApplyEnabled
              ? 'Yapay zeka profilinize uygun ilanları buluyor ve sizin adınıza başvuru yapıyor. Günlük en fazla 10 başvuru yapılır.'
              : 'Aktif edin, yapay zeka sizin adınıza uygun ilanlara otomatik başvuru yapsın.'}
          </p>
        </div>

        {/* Stats */}
        {totalApplied > 0 && (
          <div className="applications__stats">
            <div className="applications__stat">
              <Send size={18} className="applications__stat-icon" style={{ color: '#2F3A8F' }} />
              <div>
                <span className="applications__stat-value">{totalApplied}</span>
                <span className="applications__stat-label">Toplam Başvuru</span>
              </div>
            </div>
            <div className="applications__stat">
              <Eye size={18} className="applications__stat-icon" style={{ color: '#00C9A7' }} />
              <div>
                <span className="applications__stat-value">{totalViewed}</span>
                <span className="applications__stat-label">Görüntülenen</span>
              </div>
            </div>
            <div className="applications__stat">
              <Sparkles size={18} className="applications__stat-icon" style={{ color: '#8B5CF6' }} />
              <div>
                <span className="applications__stat-value">{totalInterview}</span>
                <span className="applications__stat-label">Mülakat Daveti</span>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="applications__filters">
          {[
            { key: 'all', label: 'Tümü' },
            { key: 'applied', label: 'Başvuruldu' },
            { key: 'viewed', label: 'Görüntülendi' },
            { key: 'interview', label: 'Mülakat' },
            { key: 'accepted', label: 'Kabul' },
            { key: 'rejected', label: 'Olumsuz' },
          ].map(f => (
            <button
              key={f.key}
              className={`applications__filter ${filter === f.key ? 'applications__filter--active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="applications__loading">
            <Loader2 size={24} className="applications__spinner" />
            <p>Başvurular yükleniyor...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="applications__empty">
            <Briefcase size={44} />
            <h3>{filter === 'all' ? 'Henüz başvurun yok' : 'Bu kategoride başvuru bulunamadı'}</h3>
            <p>
              {filter === 'all'
                ? autoApplyEnabled
                  ? 'Yapay zeka uygun ilanları arıyor. Başvurular yapıldıkça burada listelenecek.'
                  : 'Otomatik başvuruyu aktif edin veya ilanlara göz atarak başvurularınızı başlatın.'
                : 'Diğer filtreleri deneyebilirsiniz.'}
            </p>
            {filter === 'all' && !autoApplyEnabled && (
              <div className="applications__empty-actions">
                <button className="applications__cta applications__cta--primary" onClick={handleEnableAutoApply}>
                  <Zap size={16} /> Otomatik Başvuruyu Aktif Et
                </button>
                <Link to="/" className="applications__cta applications__cta--secondary">
                  <Briefcase size={16} /> İlanlara Göz At
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="applications__list">
            {filtered.map((app) => {
              const status = statusConfig[app.status] || statusConfig.applied;
              const StatusIcon = status.icon;
              const isExpanded = expandedId === app.id;

              return (
                <div key={app.id} className={`applications__card ${isExpanded ? 'applications__card--expanded' : ''}`}>
                  <button className="applications__card-header" onClick={() => setExpandedId(isExpanded ? null : app.id)}>
                    <div className="applications__card-main">
                      <div className="applications__card-left">
                        <h3 className="applications__card-title">{app.jobTitle}</h3>
                        <div className="applications__card-meta">
                          {app.city && (
                            <span><MapPin size={13} /> {app.city}</span>
                          )}
                          <span><Calendar size={13} /> {formatDate(app.appliedAt)}</span>
                          {app.matchScore && (
                            <span className="applications__card-score">
                              <TrendingUp size={13} /> %{app.matchScore} uyum
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="applications__card-right">
                        <div className="applications__card-status" style={{ background: status.bg, color: status.color }}>
                          <StatusIcon size={13} />
                          {status.label}
                        </div>
                        <div className="applications__card-chevron">
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="applications__card-body">
                      {app.jobDescription && (
                        <div className="applications__card-desc">
                          <p>{app.jobDescription}</p>
                        </div>
                      )}
                      <div className="applications__card-footer">
                        {app.jobUrl && (
                          <a href={app.jobUrl} target="_blank" rel="noopener noreferrer" className="applications__card-link">
                            <ExternalLink size={13} /> İlan Detayı
                          </a>
                        )}
                        {app.appliedCvName && (
                          <span className="applications__card-cv">
                            CV: {app.appliedCvName}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Consent Modal */}
        {showConsent && (
          <div className="applications__consent-overlay" onClick={() => setShowConsent(false)}>
            <div className="applications__consent" onClick={e => e.stopPropagation()}>
              <h3 className="applications__consent-title">Otomatik Başvuru Yetkisi</h3>
              <div className="applications__consent-body">
                <p>isBulsun Yapay Zeka'nın sizin adınıza aşağıdaki işlemleri yapmasını onaylıyorsunuz:</p>
                <ul>
                  <li>Profilinize uygun iş ilanlarını bulma ve eşleştirme</li>
                  <li>CV ve profil bilgilerinizi kullanarak sizin adınıza iş başvurusu yapma</li>
                  <li>Başvuru durumlarını takip etme ve size raporlama</li>
                  <li>İlana özel CV optimizasyonu yapma</li>
                </ul>
                <p className="applications__consent-note">
                  Günlük en fazla 10 başvuru yapılır. Bu yetki dilediğiniz zaman iptal edilebilir. Kişisel verileriniz KVKK kapsamında korunmaktadır.
                </p>
              </div>
              <div className="applications__consent-actions">
                <button className="applications__consent-btn applications__consent-btn--primary" onClick={handleConsent}>
                  Onaylıyorum ve Aktif Et
                </button>
                <button className="applications__consent-btn applications__consent-btn--secondary" onClick={() => setShowConsent(false)}>
                  Vazgeç
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ApplicationsPage;
